import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { XpFlyup } from "@/components/scenario/XpFlyup";
import { fireCorrectConfetti, fireStreakConfetti } from "@/utils/celebrations";

interface ExplanationPanelProps {
  correct: boolean;
  explanation: string;
  streak: number;
  xpEarned: number;
  onNext: () => void;
}

const STREAK_MILESTONES = [5, 10, 15, 20, 25, 50, 100];

export function ExplanationPanel({ correct, explanation, streak, xpEarned, onNext }: ExplanationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    if (correct) {
      if (STREAK_MILESTONES.includes(streak)) {
        fireStreakConfetti(streak);
      } else {
        fireCorrectConfetti();
      }
    }
  }, [correct, streak]);

  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const isStreakMilestone = STREAK_MILESTONES.includes(streak);

  return (
    <div ref={panelRef} className={correct ? "animate-flash-green" : "animate-shake"}>
      <Card className={correct ? "border-2 border-green-400 bg-green-50" : "border-2 border-red-400 bg-red-50"}>
        <div className="mb-3 flex items-center gap-3">
          <span className={`animate-checkmark-pop flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold text-white ${correct ? "bg-green-500" : "bg-red-500"}`}>
            {correct ? "\u2713" : "\u2717"}
          </span>
          <div className="flex-1">
            <span className={`text-lg font-bold ${correct ? "text-green-700" : "text-red-700"}`}>
              {correct ? "Correct!" : "Incorrect"}
            </span>
            {streak > 1 && (
              <p className="text-sm font-medium text-orange-600">
                {isStreakMilestone ? `${streak} in a row!` : `${streak} in a row!`}
              </p>
            )}
          </div>
          <XpFlyup xp={xpEarned} />
        </div>

        <p className={`mb-4 text-sm ${correct ? "text-green-800" : "text-red-800"}`}>{explanation}</p>

        <Button onClick={onNext} className="w-full">
          Next Scenario
        </Button>
      </Card>
    </div>
  );
}
